import type { CreateFantasyTeamRequest, StartClaimRequest } from '@brush/api-client';
import { useActingRole } from '@brush/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useApi, useSession } from './api';
import { erasurePhase } from './erasure';

/**
 * Which build the API is running, for the About panel. Enabled only while the panel is open — it
 * is the one place the answer is shown, and an app that asked on every load would spend a request
 * per session on a screen most people never open.
 *
 * `staleTime: Infinity`: a running API does not change build under a page. A reload is what picks
 * up a new one, and a reload refetches anyway.
 */
export function useApiVersion(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['api-version'],
    queryFn: () => api.version(),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/** Who the caller is. Admins and operators browse the portal read-only. */
export function useCurrentUser() {
  const api = useApi();
  const { actingRole } = useActingRole();

  // Keyed on the acting role, because the answer depends on it — and because clearing a refused
  // choice then changes the key, so the refetch that discovers what this account may still do
  // needs nobody to remember to ask for it.
  return useQuery({
    queryKey: ['me', actingRole ?? 'unchosen'],
    queryFn: () => api.me(),
    staleTime: 5 * 60_000,
    // A refused choice is answered by clearing it, not by asking again with the same header.
    retry: false,
  });
}

/**
 * Scoped player search for the picker. The server limits results to the rooms the caller may
 * see and caps the page size, so this never returns the whole table.
 */
export function usePlayerSearch(query: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['player-search', query],
    queryFn: () => api.players.search(query || undefined),
    enabled,
    staleTime: 30_000,
  });
}

/** The player currently being viewed. */
export function usePlayerById(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['player', id],
    queryFn: () => api.players.get(id ?? ''),
    enabled: !!id,
  });
}

/**
 * The room directory — the public projection, and the only room read this app makes.
 *
 * `GET /api/rooms` is staff-only since ADR 0010's read tier, which is correct and not a
 * restriction on the portal: what this app needs is a room's name and colours, and what that
 * endpoint adds is every room's player and tournament counts.
 */
export function useRooms() {
  const api = useApi();
  return useQuery({ queryKey: ['rooms'], queryFn: () => api.rooms.directory() });
}

/**
 * The caller's own results at a room — a page newest first beside totals over every result — for Home and
 * Stats (known gap 78). It replaced the room's capped tournament list, which carried every result in the
 * room (1.9 MB for SHRPO Demo, up to 52 s on staging) and lost a player's oldest finishes past 200
 * tournaments.
 *
 * <b>Only for the caller's own portal</b> (`enabled`): the read is the card the caller's sign-in holds, so on
 * somebody else's portal — staff browsing — the API answers `room_not_a_member`, which the membership
 * recheck would take as a revoked card. The screens say instead whose results these are (`readState`).
 * The previous page stays on screen while the next arrives, so paging Stats never blanks the history — but
 * only within one room: another room's results under a newly chosen room's name would be somebody's history
 * shown as the wrong card's.
 */
export function useOwnResults(roomId: string | null, page: number, pageSize: number, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['own-results', roomId, page, pageSize],
    queryFn: () => api.players.ownResults(roomId ?? '', page, pageSize),
    enabled: enabled && !!roomId,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === roomId ? previous : undefined),
  });
}

export function useLeaderboards(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboards', roomId],
    queryFn: () => api.leaderboards.list(roomId ?? undefined),
    enabled: !!roomId,
  });
}

export function useLeaderboardStandings(leaderboardId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboard-standings', leaderboardId],
    queryFn: () => api.leaderboards.standings(leaderboardId ?? ''),
    enabled: !!leaderboardId,
  });
}

/**
 * §4.7's boards at this room that this reader may see — the portal's picker.
 *
 * A different endpoint from the staff list and a much smaller shape: `RatingBoardSummary` carries a
 * name, a status and whether anything has been published, where `RatingBoard` carries §4's five
 * configuration documents, the recalculation cron and the board's author. Same split as the room
 * directory against `GET /api/rooms`, and the projection is the control rather than this query.
 *
 * Filtered rather than refused, so a room with nothing published answers an empty list.
 */
export function useVisibleRatingBoards(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-boards-visible', roomId],
    queryFn: () => api.ratingBoards.visible(roomId ?? ''),
    enabled: !!roomId,
  });
}

/**
 * §8's standings: <b>the latest snapshot, read back</b> — what this rating <i>does</i> say.
 *
 * Deliberately not `/compute`, which runs the engine and answers what it <i>would</i> say. That route
 * is staff-only and stays so: a player must read the figures their room published, and a screen that
 * recomputed on every read would move under whoever was looking at it.
 *
 * Null where the board has never been recalculated — it has published nothing, which is not the same
 * as nobody having scored, and the screen words the two apart.
 */
export function useRatingStandings(boardId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-standings', boardId],
    queryFn: () => api.ratingBoards.standings(boardId ?? ''),
    enabled: !!boardId,
  });
}

/**
 * §7. One player's rating taken apart — period by period, with every result that bucketed in,
 * <b>including the ones selection dropped</b>, and the points engine's own working nested inside each.
 *
 * <b>Keyed under the snapshot, which is what makes "fetched once" structural rather than a window.</b>
 * The sequence number is part of the key, so a recalculation produces a different key rather than
 * staling this one — and a frozen snapshot's breakdown is frozen too, which is why `staleTime:
 * Infinity` is correct here rather than optimistic. Reopening a row therefore costs no request at any
 * remove; the app's global 30-second `staleTime`, which is all the leaderboard breakdown has, is a
 * window and not a guarantee.
 *
 * The scoping id comes before the discriminator, so invalidating `['rating-standings', boardId]`
 * reaches every breakdown of that board.
 *
 *
 * <b>`gcTime` is left at its default five minutes and is deliberately not `Infinity`.</b> So a row
 * reopened straight away costs no request — which is the requirement — and one reopened after five
 * minutes with nothing else observing it does cost one, because by then the document is no longer in
 * memory and re-reading it is the only honest thing left to do. Holding every breakdown a board's
 * operator ever opened would be an unbounded amount of `nvarchar(max)` in a tab somebody leaves open
 * all day.
 *
 * <b>Only ever asked about the reader's own card</b> — the server refuses anybody else's with a 403
 * `breakdown_not_yours` (ADR 0011 Decision 10), and `offersBreakdown` is what keeps the portal from
 * offering a control whose answer is always that.
 */
export function useRatingBreakdown(
  boardId: string | null,
  sequenceNumber: number | null,
  playerId: string | null,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-standings', boardId, sequenceNumber, 'breakdown', playerId],
    queryFn: () => api.ratingBoards.breakdown(boardId ?? '', playerId ?? ''),
    enabled: !!boardId && !!playerId && sequenceNumber !== null,
    staleTime: Infinity,
  });
}

/**
 * §7.4. One player's total taken apart event by event.
 *
 * Fetched only when a row is opened — a board is hundreds of players and nobody reads more than
 * their own. It comes out of the same snapshot the standings did, so it is exactly as current as
 * what is already on screen beside it.
 */
export function useLeaderboardBreakdown(leaderboardId: string | null, playerId: string | null) {
  const api = useApi();
  return useQuery({
    // The scoping id first, then the discriminator: invalidating ['leaderboard-breakdown', id]
    // has to reach every player's breakdown of that board.
    queryKey: ['leaderboard-breakdown', leaderboardId, playerId],
    queryFn: () => api.leaderboards.breakdown(leaderboardId ?? '', playerId ?? ''),
    enabled: !!leaderboardId && !!playerId,
  });
}

export function useFantasyContests(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-contests', roomId],
    queryFn: () => api.fantasyContests.list(roomId ?? undefined),
    enabled: !!roomId,
  });
}

/** One contest, which is where `standingsPaused` lives — see `useFantasyStandings`. */
export function useFantasyContest(contestId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-contest', contestId],
    queryFn: () => api.fantasyContests.get(contestId!),
    enabled: !!contestId,
  });
}

/**
 * The frozen price list an entrant drafts from.
 *
 * `staleTime: Infinity`: publishing is what freezes it, so for every contest a player can enter it
 * cannot change while they are looking at it. Refetching a 200-row list on every window focus
 * would be a request spent to learn nothing.
 */
export function useFantasyPool(contestId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-pool', contestId],
    queryFn: () => api.fantasyContests.pool.get(contestId!),
    enabled: !!contestId,
    staleTime: Infinity,
  });
}

export function useFantasyStandings(contestId: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-standings', contestId],
    queryFn: () => api.fantasyContests.standings(contestId!),
    enabled: enabled && !!contestId,
  });
}

/**
 * The audit trail of what was computed when — <b>rows deliberately omitted by the server</b>, so
 * this is cheap and is asked for only to learn which snapshot came before the current one.
 *
 * §9.2 gets movement "free because prior snapshots are retained", and this is the price of free:
 * one list, then one older snapshot. Sequence numbers are read here and never rendered (§9.3).
 */
export function useFantasySnapshots(contestId: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-snapshots', contestId],
    queryFn: () => api.fantasyContests.snapshots(contestId!),
    enabled: enabled && !!contestId,
  });
}

/** One numbered snapshot, with its rows — the previous board, for movement. */
export function useFantasySnapshot(contestId: string | null, sequenceNumber: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-snapshot', contestId, sequenceNumber],
    queryFn: () => api.fantasyContests.snapshot(contestId!, sequenceNumber!),
    enabled: !!contestId && sequenceNumber !== null,
    // A frozen snapshot is frozen. Nothing about an older one can change.
    staleTime: Infinity,
  });
}

/**
 * §8.3's check, answered rather than thrown and writing nothing.
 *
 * <b>A query keyed on the lineup, debounced by the caller.</b> The client mirrors these rules in
 * `fantasy/roster.ts` so the budget meter moves on a click, and this is the control that mirror is
 * feedback for — so it has to run on the roster as it stands rather than on submit, where a
 * disagreement would surface as a bounced submission.
 *
 * `staleTime: Infinity` because the pool is frozen and the rules are fixed: returning to a lineup
 * already checked cannot produce a different answer. `retry: false` for the reason the points
 * preview has it — a refused request is not made better by being made again.
 */
export function useValidateFantasyRoster({
  contestId,
  playerIds,
  captainPlayerId,
  enabled,
}: {
  contestId: string | null;
  playerIds: readonly string[];
  captainPlayerId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    // Sorted, because a roster is a set: reordering the same eight players is the same lineup and
    // must not spend a second request or blank the answer already on screen.
    queryKey: ['fantasy-validate', contestId, [...playerIds].sort(), captainPlayerId],
    queryFn: () =>
      api.fantasyTeams.validate(contestId!, {
        // The contract wants one and no roster rule reads it — `FantasyRosterValidator.Check`
        // takes a size, a budget, a pool and a captain, and nothing else. A constant rather than
        // the box's current value, which would be a stale closure over a field that changes the
        // answer to nothing and is not in the key.
        name: 'Lineup',
        playerIds: [...playerIds],
        captainPlayerId,
      }),
    enabled: enabled && !!contestId,
    staleTime: Infinity,
    retry: false,
    // Keeps the last answer while the next is worked out, rather than blanking the problems list
    // between picks.
    placeholderData: (previous) => previous,
  });
}

/** §8.4's audit trail for one team. Read from the frozen snapshot; nothing here is recomputed. */
export function useFantasyTeamBreakdown(teamId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-team-breakdown', teamId],
    queryFn: () => api.fantasyTeams.breakdown(teamId!),
    enabled: !!teamId,
  });
}

/** Withdrawing an entry. 409 once the contest locks — by then it is a standing. */
export function useDeleteFantasyTeam(contestId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => api.fantasyTeams.remove(teamId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fantasy-teams', contestId] });
      void qc.invalidateQueries({ queryKey: ['fantasy-contests'] });
      void qc.invalidateQueries({ queryKey: ['fantasy-contest', contestId] });
    },
  });
}

/**
 * This player's own entries in one contest.
 *
 * It names the contest and nothing else — no `userId`, which is the change rather than an
 * omission: the endpoint used to answer about whoever a query string named, so any signed-in
 * caller could read another account's teams (known gap 1's read half, ADR 0010 Decision 5). The
 * server scopes the answer to the cards the token holds, so "mine" is decided there and cannot be
 * asked for.
 */
export function useMyFantasyTeams(contestId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-teams', contestId],
    queryFn: () => api.fantasyTeams.list(contestId!),
    enabled: !!contestId,
  });
}

/**
 * Upsert keyed on (contest, entrant, entryIndex) — the server replaces an existing entry.
 *
 * The entrant is the caller's own card at the contest's room, resolved from the token. Nothing
 * here names it, and `CreateFantasyTeamRequest` has no field for it.
 */
export function useUpsertFantasyTeam(contestId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFantasyTeamRequest) => api.fantasyTeams.upsert(contestId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fantasy-teams', contestId] });
      void qc.invalidateQueries({ queryKey: ['fantasy-contests'] });
      void qc.invalidateQueries({ queryKey: ['fantasy-contest', contestId] });
    },
  });
}

/**
 * Starts a claim on a card (ADR 0010, Decision 3).
 *
 * <b>Nothing is invalidated on success, because nothing has happened yet.</b> A 202 here says a
 * challenge exists; it deliberately does not say a code was sent, that anybody matched, or that
 * this card can be connected at all — so a refetch of `/api/me` would be a request asking a
 * question whose answer cannot have changed.
 *
 * No retry: the per-caller limit is five starts a quarter hour, and a retry into a 429 spends an
 * allowance the player will need to finish what they started.
 */
export function useStartClaim() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: StartClaimRequest) => api.memberships.startClaim(body),
    retry: false,
  });
}

/**
 * Finishes one with the code. This is the call that creates the membership, so `/api/me` is what
 * changes — and it is the whole of what changes: the player and room queries are keyed by id and
 * will be asked for the first time rather than re-asked.
 */
export function useCompleteClaim() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challengeId: string; code: string }) =>
      api.memberships.completeClaim(vars.challengeId, { code: vars.code }),
    retry: false,
    // Returned rather than fired and forgotten, so the mutation stays pending until `/api/me`
    // carries the new membership. Without that the success screen's "Open my portal" can land
    // before the refetch does, and the portal resolves from a list that has not grown yet — a
    // flash of the discovery view immediately after connecting a card, which is the one moment it
    // reads as the claim having failed.
    //
    // Prefix-invalidated: the key carries the acting role after 'me', so this reaches whichever
    // role asked.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/**
 * Disconnects the caller from their card at one room — one row, and deliberately nothing else
 * (Decision 7). No result, standing or snapshot moves, so nothing else needs invalidating.
 */
/**
 * Erases the caller's own account (ADR 0012 step 28): a fresh sign-in first, then the delete carrying that
 * sign-in's token. Nothing is invalidated on success — the portal signs out at once, and a refetch of
 * `/api/me` in between would only race the redirect.
 *
 * `phase` says which half is under way, because the sign-in can take five minutes and end with nothing
 * deleted (`erasurePhase` has the reason). It is recorded inside the mutation, at the one moment the
 * sign-in's token is in hand, and cleared when an attempt starts — so a second attempt does not inherit
 * the first one's "signed in".
 */
export function useEraseAccount() {
  const api = useApi();
  const { signInAgain } = useSession();
  const [signedIn, setSignedIn] = useState(false);
  const mutation = useMutation({
    mutationFn: async () => {
      setSignedIn(false);
      const token = await signInAgain();
      setSignedIn(true);
      return api.account.erase(token);
    },
    retry: false,
  });
  return { ...mutation, phase: erasurePhase(mutation.isPending, signedIn) };
}

export function useUnlinkMembership() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => api.memberships.unlink(roomId),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
